import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { BetaFeedback } from '../types'

interface UseBetaFeedbackOptions {
  status?: 'all' | 'open' | 'in_progress' | 'resolved'
  search?: string
}

export function useBetaFeedback(client: SupabaseClient | null, options: UseBetaFeedbackOptions = {}) {
  const { status = 'all', search = '' } = options

  const [feedback, setFeedback] = useState<BetaFeedback[]>([])
  const [stats, setStats] = useState({ open: 0, inProgress: 0, resolved: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFeedback = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client.from('beta_feedback').select('*')

      if (status !== 'all') {
        query = query.eq('status', status)
      }

      if (search) {
        query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`)
      }

      query = query.order('submitted_at', { ascending: false })

      const { data, error: queryError } = await query

      if (queryError) throw queryError
      setFeedback(data || [])

      // Fetch stats
      const [{ count: openCount }, { count: inProgressCount }, { count: resolvedCount }] = await Promise.all([
        client.from('beta_feedback').select('*', { count: 'exact', head: true }).eq('status', 'open'),
        client.from('beta_feedback').select('*', { count: 'exact', head: true }).eq('status', 'in_progress'),
        client.from('beta_feedback').select('*', { count: 'exact', head: true }).eq('status', 'resolved'),
      ])

      setStats({
        open: openCount || 0,
        inProgress: inProgressCount || 0,
        resolved: resolvedCount || 0,
      })
    } catch (err: any) {
      console.error('Error fetching beta feedback:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, status, search])

  useEffect(() => {
    fetchFeedback()
  }, [fetchFeedback])

  const updateStatus = useCallback(async (id: string, newStatus: BetaFeedback['status'], adminNotes?: string) => {
    if (!client) return false

    try {
      const updates: any = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      }
      if (adminNotes !== undefined) {
        updates.admin_notes = adminNotes
      }

      const { error: updateError } = await client
        .from('beta_feedback')
        .update(updates)
        .eq('id', id)

      if (updateError) throw updateError
      await fetchFeedback()
      return true
    } catch (err: any) {
      console.error('Error updating feedback status:', err)
      return false
    }
  }, [client, fetchFeedback])

  return { feedback, stats, isLoading, error, refetch: fetchFeedback, updateStatus }
}
